import { Seat, type TSeat } from './types';

export interface ISettlementInput {
  readonly winnerSeat: TSeat;
  readonly loserSeat: TSeat | null;
  readonly isSelfDraw: boolean;
  readonly handTai: number;
  readonly dealerSeat: TSeat;
  readonly dealerStreak: number;
  readonly base: number;
  readonly taiUnit: number;
  readonly balancesBefore: readonly [number, number, number, number];
}

export interface ISettlementResult {
  readonly deltaBySeat: readonly [number, number, number, number];
  readonly balancesAfter: readonly [number, number, number, number];
  readonly dealerRelationTai: number;
}

const ALL_SEATS = [Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as const;

export const dealerRelationTaiFor = (dealerStreak: number): number => {
  if (!Number.isInteger(dealerStreak) || dealerStreak < 0) {
    throw new Error(`Invalid dealer streak: ${dealerStreak}`);
  }
  return dealerStreak === 0 ? 1 : dealerStreak * 2 + 1;
};

export const computeSettlement = (input: ISettlementInput): ISettlementResult => {
  const delta: [number, number, number, number] = [0, 0, 0, 0];
  const dealerRelationTai = dealerRelationTaiFor(input.dealerStreak);
  const normalPayment = input.base + input.handTai * input.taiUnit;
  const dealerPayment = input.base + (input.handTai + dealerRelationTai) * input.taiUnit;

  const transfer = (payer: TSeat, amount: number): void => {
    delta[payer] -= amount;
    delta[input.winnerSeat] += amount;
  };

  if (input.isSelfDraw) {
    for (const payer of ALL_SEATS) {
      if (payer === input.winnerSeat) continue;
      const includesDealerRelation =
        input.winnerSeat === input.dealerSeat || payer === input.dealerSeat;
      transfer(payer, includesDealerRelation ? dealerPayment : normalPayment);
    }
  } else {
    if (input.loserSeat === null) {
      throw new Error('Discard win requires loserSeat');
    }
    const includesDealerRelation =
      input.winnerSeat === input.dealerSeat || input.loserSeat === input.dealerSeat;
    transfer(input.loserSeat, includesDealerRelation ? dealerPayment : normalPayment);
  }

  const balancesAfter: [number, number, number, number] = [
    input.balancesBefore[0] + delta[0],
    input.balancesBefore[1] + delta[1],
    input.balancesBefore[2] + delta[2],
    input.balancesBefore[3] + delta[3],
  ];

  return { deltaBySeat: delta, balancesAfter, dealerRelationTai };
};
